import { z } from 'zod';

export type MongoObjectId = { $oid: string };

export const MONGO_OBJECT_ID_PATTERN = /^[a-f\d]{24}$/i;
export const MONGO_OBJECT_ID_HEX_PREFIX_PATTERN = /^[a-f\d]{1,24}$/i;

export const MongoObjectIdSchema = z
  .string()
  .regex(MONGO_OBJECT_ID_PATTERN, 'Invalid ObjectId');

export const isMongoObjectId = (value: string): boolean =>
  MONGO_OBJECT_ID_PATTERN.test(value);

export const readMongoObjectId = (value: unknown): string | null => {
  if (typeof value === 'string') {
    return isMongoObjectId(value) ? value : null;
  }

  if (
    typeof value !== 'object' ||
    value === null ||
    !Object.hasOwn(value, '$oid')
  ) {
    return null;
  }

  const oid = (value as Record<string, unknown>)['$oid'];

  return typeof oid === 'string' && isMongoObjectId(oid) ? oid : null;
};

export const isMongoObjectIdHexPrefix = (value: string): boolean =>
  MONGO_OBJECT_ID_HEX_PREFIX_PATTERN.test(value.trim());

export const escapeRegexForMongoObjectIdSearch = (value: string): string =>
  value.replace(/[\\()[\]{}.*+?^$|]/g, '\\$&');

export const buildMongoObjectIdFieldPrismaCondition = ({
  field,
  searchTerm,
}: {
  field: string;
  searchTerm: string;
}): Record<string, unknown> | undefined => {
  const trimmed = searchTerm.trim();

  if (!isMongoObjectIdHexPrefix(trimmed)) {
    return undefined;
  }

  if (isMongoObjectId(trimmed)) {
    return { [field]: trimmed };
  }

  return { [field]: { contains: trimmed, mode: 'insensitive' as const } };
};

export const buildMongoObjectIdMongoIdMatch = (
  searchTerm: string,
): Record<string, unknown> | undefined => {
  const trimmed = searchTerm.trim();

  if (!isMongoObjectIdHexPrefix(trimmed)) {
    return undefined;
  }

  if (isMongoObjectId(trimmed)) {
    return { _id: { $oid: trimmed } };
  }

  const escaped = escapeRegexForMongoObjectIdSearch(trimmed);

  return {
    $expr: {
      $regexMatch: {
        input: { $toString: '$_id' },
        regex: escaped,
        options: 'i',
      },
    },
  };
};
